import { rangeToResampleMapping } from '@toggle/chart';
import { SmartTable, Tooltip } from '@toggle/design-system';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router';

import { Filter } from '~/components/filter/Filter';
import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { ScenariosData } from '~/react-query-hooks/scenarios/useScenarios';
import { useMultipleSnakesMeta } from '~/react-query-hooks/snakes/useMultipleSnakesMeta';
import { appPaths } from '~/routes/app-paths';
import { PAST_EPISODES_HORIZONS } from '~/widgets/past-episodes-widget/components/insight-past-episodes/InsightPastEpisodes';
import {
  getColumns,
  getHorizonsLabels,
  mapEpisodesToTableRow,
} from '~/widgets/past-episodes-widget/components/insight-past-episodes/utils/utils';
import { PastEpisodesChart } from '~/widgets/past-episodes-widget/components/past-episodes-chart/PastEpisodesChart';
import {
  DEFAULT_EPISODES_TO_SHOW,
  EMPTY_FILTER_LABELS,
  useCollapsibleEpisodesTable,
} from '~/widgets/past-episodes-widget/hooks/useCollapsibleEpisodesTable';
import * as S from '~/widgets/past-episodes-widget/PastEpisodesWidget.styles';

export interface ScenarioPastEpisodeSectionProps {
  snakes: string[];
  scenario: ScenariosData;
}

export const ScenarioPastEpisodeSection = ({
  scenario,
  snakes,
}: ScenarioPastEpisodeSectionProps) => {
  const { t } = useTranslation(['scenario', 'widget', 'chart']);
  const navigate = useNavigate();
  const [activeHorizonIdx, setActiveHorizonIdx] = useState(5);
  const {
    isCollapsed,
    tableRef,
    activeSortOrderOption,
    setActiveSortOrderOption,
    sortOrderFilter,
    handleViewMoreClick,
  } = useCollapsibleEpisodesTable();

  const { data, isFetching } = useMultipleSnakesMeta(snakes);

  const handleChangeHorizon = (index: number) => setActiveHorizonIdx(index);

  const handleFormatAssetsParams = () => {
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((meta, index) => `${meta.entity};${meta.snake};${index}`);
  };

  const assets = isFetching
    ? undefined
    : data.map((meta, index) => ({
        tag: meta.entity,
        snake: meta.snake,
        paneIndex: index,
      }));

  const totalTradingDays =
    scenario.episodes.reduce((acc, ep) => acc + ep.events.length, 0) || 0;

  const formattedEpisodes = useMemo(
    () =>
      scenario.episodes
        .map(episode => {
          const endDate = episode.events.reduce((latest, event) => {
            return event.date > latest.date ? event : latest;
          }).date;

          return {
            start_date: episode.date,
            end_date: endDate,
            events: episode.events,
          };
        })
        .sort((a, b) =>
          activeSortOrderOption.value.id === 'asc'
            ? a.start_date.localeCompare(b.start_date)
            : b.start_date.localeCompare(a.start_date)
        ),
    [scenario.episodes, activeSortOrderOption.value.id]
  );

  const columns = getColumns(totalTradingDays, t);

  const episodesToShow = isCollapsed
    ? formattedEpisodes.slice(0, DEFAULT_EPISODES_TO_SHOW)
    : formattedEpisodes;

  return (
    <>
      <S.Title data-testid="scenario-past-episode">
        <span>
          <span>{t('widget:pastEpisode:title.occurrences')}</span>
          <S.PastEpisodeKey />
          <TooltipContainer
            label={t('analyze:experiment.chart.tooltip')}
            showUnderline
          >
            <span>
              {t('widget:pastEpisode:title.episodes', {
                episodes: scenario.episodes.length,
              })}
            </span>
          </TooltipContainer>
          <span>
            {t('widget:pastEpisode:title.days', {
              totalTradingDays,
            })}
          </span>
        </span>
      </S.Title>
      <S.PastEpisodesButtons>
        <S.StyledButtonGroup
          labels={getHorizonsLabels(t, PAST_EPISODES_HORIZONS)}
          onClick={handleChangeHorizon}
          activeIndex={activeHorizonIdx}
          $activeIndex={activeHorizonIdx}
        />
        <Tooltip label={t('chart:fullscreen')}>
          <S.StyledButton
            aria-label={t('chart:fullscreen')}
            iconName="Expand"
            variant="empty"
            size="small"
            onClick={() => {
              navigate({
                pathname: appPaths.chart,
                search: createSearchParams({
                  asset: handleFormatAssetsParams(),
                  horizon: PAST_EPISODES_HORIZONS[activeHorizonIdx],
                  primary: snakes,
                  resample:
                    rangeToResampleMapping[
                      PAST_EPISODES_HORIZONS[activeHorizonIdx]
                    ],
                  'yAxisType.1': 'merged',
                }).toString(),
              });
            }}
          />
        </Tooltip>
      </S.PastEpisodesButtons>
      <S.ChartWrapper>
        <PastEpisodesChart
          assets={assets}
          episodes={formattedEpisodes}
          horizon={PAST_EPISODES_HORIZONS[activeHorizonIdx]}
        />
      </S.ChartWrapper>
      <S.Header>
        <S.Title>{t('widget:pastEpisode.pastEpisode')}</S.Title>
        <Filter
          filter={sortOrderFilter}
          labels={EMPTY_FILTER_LABELS}
          filterOptions={[activeSortOrderOption]}
          addFilterOption={({ option }) => setActiveSortOrderOption(option)}
          removeFilterOption={() => {}}
          onCustomRangeClick={() => {}}
          contentStyle={S.filterDropdownContentStyle}
        />
      </S.Header>
      <>
        <S.TableRoot $isCollapsed={isCollapsed} ref={tableRef}>
          <SmartTable
            columns={columns}
            data={mapEpisodesToTableRow(episodesToShow)}
            withBodyRowHover={false}
            isHeaderSticky
          />
        </S.TableRoot>
        {formattedEpisodes.length > DEFAULT_EPISODES_TO_SHOW && (
          <S.ViewMoreButton
            label={t(
              `widget:pastEpisode.table.${
                isCollapsed ? 'viewMore' : 'viewLess'
              }`
            )}
            onClick={handleViewMoreClick}
          />
        )}
      </>
    </>
  );
};
